import { ProjectLabel } from "@/components/ProjectList/types";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { onProjectsSnapshot, updateCanvasFields } from "@/store/supabaseSlice";
import { Canvas, Project } from "@/utils/types";
import { toast } from "sonner";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";

export const useKanbanActions = () => {
  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
  const dispatch = useDispatch();

  const updateCanvasAssignedUserId = async (
    canvas: Canvas,
    newUserId: string | null
  ) => {
    if (!supabaseClient || !canvas?.id) return;

    const previousUserId = canvas.assigned_user_id;

    dispatch(
      updateCanvasFields({
        id: canvas.id,
        fields: {
          assigned_user_id: newUserId,
          last_updated: new Date().toISOString(),
        },
      })
    );

    const { error } = await supabaseClient
      .from("canvases")
      .update({
        assigned_user_id: newUserId,
        last_updated: new Date().toISOString(),
      })
      .eq("id", canvas.id);

    if (error) {
      console.error("Error updating assigned user in DB:", error);
      toast.error("Failed to assign user. Reverting change.");

      dispatch(
        updateCanvasFields({
          id: canvas.id,
          fields: {
            assigned_user_id: previousUserId,
            last_updated: canvas.last_updated,
          },
        })
      );
    }
  };

  const updateCanvasLabels = async (canvas: Canvas, newLabels: string[]) => {
    if (!supabaseClient || !canvas?.id) return;

    dispatch(
      updateCanvasFields({
        id: canvas.id,
        fields: {
          labels: newLabels,
          last_updated: new Date().toISOString(),
        },
      })
    );

    const { error } = await supabaseClient
      .from("canvases")
      .update({ labels: newLabels, last_updated: new Date().toISOString() })
      .eq("id", canvas.id);

    if (error) {
      console.error("Error updating canvas labels in DB:", error);
      toast.error("Failed to update labels. Reverting change.");

      dispatch(
        updateCanvasFields({
          id: canvas.id,
          fields: {
            labels: canvas.labels,
            last_updated: canvas.last_updated,
          },
        })
      );
    }
  };

  const updateProjectLabels = async (
    project: Project,
    currentLabels: ProjectLabel[],
    newLabelData: Omit<ProjectLabel, "id">
  ) => {
    if (!supabaseClient) return undefined;

    const trimmedLabel = newLabelData.label.trim();
    if (!trimmedLabel) {
      toast.error("Label name cannot be empty.");
      return undefined;
    }

    const updatedLabel = {
      ...newLabelData,
      label: trimmedLabel,
      id: uuidv4().toString(),
    };

    dispatch(
      onProjectsSnapshot({
        newItems: [
          { ...project, labels: [...(currentLabels || []), updatedLabel] },
        ],
      })
    );

    const { data: updatedProjectData, error } = await supabaseClient
      .from("projects")
      .update({
        labels: [...(currentLabels || []), updatedLabel],
      })
      .eq("id", project.id)
      .select();

    if (error) {
      console.error("Error updating project labels:", error);
      toast.error("Failed to add project label.");
      dispatch(
        onProjectsSnapshot({
          newItems: [{ ...project, labels: currentLabels }],
        })
      );
      return undefined;
    }

    if (updatedProjectData && updatedProjectData.length > 0) {
      dispatch(onProjectsSnapshot({ newItems: [updatedProjectData[0]] }));
      return updatedLabel;
    }

    toast.error("Failed to add project label for an unknown reason.");
    dispatch(
      onProjectsSnapshot({
        newItems: [{ ...project, labels: currentLabels }],
      })
    );
  };

  return {
    updateCanvasAssignedUserId,
    updateCanvasLabels,
    updateProjectLabels,
  };
};
